import { useMemo } from "react";
import { motion } from "framer-motion";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceDot,
} from "recharts";
import { TrendingDown, TrendingUp, ChevronRight } from "lucide-react";

import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

// FIN-1 — RitmoGastosLineChart
// 2 séries: Este mês (sólida) + Mês passado (pontilhada cinza). Ponto
// destacado no dia atual via ReferenceDot.

const fmtBRL = (v) => Number(v ?? 0).toLocaleString("pt-BR", {
  style: "currency", currency: "BRL", maximumFractionDigits: 0,
});

function CustomTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg bg-surface-overlay border border-subtle px-3 py-2 shadow-lg backdrop-blur-md">
      <p className="text-caption font-semibold text-foreground mb-1">Dia {payload[0]?.payload?.day}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-micro tabular-nums" style={{ color: p.color }}>
          {p.name}: {fmtBRL(p.value)}
        </p>
      ))}
    </div>
  );
}

export default function RitmoGastosLineChart({ cumulative, loading, onSeeAll }) {
  const cur = cumulative?.daily_cumulative || [];
  const prev = cumulative?.previous_month_cumulative || [];
  const totalSpent = cumulative?.total_spent ?? 0;
  const previousTotal = cumulative?.vs_previous_month?.previous_total ?? 0;
  const deltaPct = cumulative?.vs_previous_month?.delta_pct ?? 0;
  const deltaLabel = cumulative?.vs_previous_month?.delta_label || "igual";

  const today = new Date();
  const todayDay = today.getDate();

  const chartData = useMemo(() => {
    // Faz merge dos dois arrays alinhando por dia
    const maxDays = Math.max(cur.length, prev.length, 30);
    const merged = [];
    for (let d = 1; d <= maxDays; d++) {
      const c = cur.find(x => x.day === d);
      const p = prev.find(x => x.day === d);
      merged.push({
        day: d,
        "Este mês": c ? c.cumulative : null,
        "Mês passado": p ? p.cumulative : null,
      });
    }
    return merged;
  }, [cur, prev]);

  const hasData = totalSpent > 0 || previousTotal > 0;
  const todayPoint = chartData.find(d => d.day === todayDay);
  const isLower = deltaPct < 0;

  return (
    <Card variant="elevated" className="glass-card glow-hover p-5 md:p-6 flex flex-col" data-testid="ritmo-gastos-card">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <span className="text-caption font-semibold uppercase text-brand-cyan/80" style={{ letterSpacing: "0.12em" }}>
            Ritmo de gastos
          </span>
          <p className="text-h2 font-bold tabular-nums text-foreground mt-1.5">
            {fmtBRL(totalSpent)}
            {deltaLabel !== "igual" && (
              <span className={cn(
                "text-caption font-medium ml-2",
                isLower ? "text-brand-green" : "text-status-error",
              )}>
                {deltaLabel}
              </span>
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={onSeeAll}
          className="inline-flex items-center gap-1 text-caption text-brand-cyan hover:text-brand-cyan/80 transition-colors"
          data-testid="ritmo-see-all"
        >
          ver todas
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>

      <Badge
        variant="outline"
        className={cn(
          "border-subtle bg-transparent inline-flex items-center gap-1 mb-4",
          isLower ? "text-brand-green" : "text-status-error",
        )}
        data-testid="ritmo-delta"
      >
        {isLower ? <TrendingDown className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}
        {deltaPct === 0 ? "sem variação vs mês anterior" : `${Math.abs(deltaPct)}% vs ${fmtBRL(previousTotal)} mês anterior`}
      </Badge>

      {loading ? (
        <div className="flex-1 h-[200px] rounded-lg bg-surface-card-hover/40 animate-pulse" />
      ) : !hasData ? (
        <div className="flex-1 flex items-center justify-center py-8 text-center">
          <p className="text-sm text-foreground-muted max-w-xs">
            Sem comparativo ainda. Volte mês que vem pra ver evolução.
          </p>
        </div>
      ) : (
        <div className="flex-1 min-h-[200px]" data-testid="ritmo-chart">
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={chartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.04)" vertical={false} />
              <XAxis
                dataKey="day"
                stroke="#64748B"
                tick={{ fill: "#94A3B8", fontSize: 10 }}
                axisLine={false} tickLine={false}
                interval={4}
              />
              <YAxis
                stroke="#64748B"
                tick={{ fill: "#94A3B8", fontSize: 10 }}
                axisLine={false} tickLine={false}
                tickFormatter={(v) => v >= 1000 ? `${Math.round(v / 1000)}k` : v}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="monotone" dataKey="Mês passado"
                stroke="#64748B" strokeWidth={1.5} strokeDasharray="4 4"
                dot={false} connectNulls
              />
              <Line
                type="monotone" dataKey="Este mês"
                stroke="#00D4FF" strokeWidth={2}
                dot={false} connectNulls
              />
              {todayPoint && todayPoint["Este mês"] != null && (
                <ReferenceDot
                  x={todayDay} y={todayPoint["Este mês"]}
                  r={4} fill="#00D4FF" stroke="#00FFA3" strokeWidth={2}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}
